import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CompareChip from 'src/components/chip/compare-chip';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import { formatNumber, fPercent } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from 'src/components/competitive-intelligence/components/CustomTooltip';

interface SeriesData {
    x: string;
    y: number;
}

interface Props {
    val: string;
    handleKPI: (key: string) => void;
    title: string;
    total: number;
    compare: number;
    color?: string;
    isPercent?: boolean;
    isCurrency?: boolean;
    series?: SeriesData[];
    definition?: string;
    isFetching?: boolean;
    selected: any,
    invertedColor?: boolean;
    isFirst?: boolean;
    sx?: any;
    palette?: { lighter?: string; darker?: string };
}

export default function BankingWidgetSummary({ ...props }: Props) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const filterStore = useSelector((state: any) => state.filters);
    const {
        val, title, selected, total, compare, color, series, isPercent,
        isCurrency, definition, isFetching, isFirst = true,
        invertedColor, sx, handleKPI, palette, ...otherProps
    } = props;
    const workspace: any = useCurrentWorkspace();
    const lang = i18n.language || 'en';
    const currency = workspace?.currency || 'USD';


    const formatValue = (value: number, isPercentFlag: boolean) => {
        return formatTitle(title, value);
    };

    const compareValue = (totalVal: number, compareVal: number) => {
        if (compareVal) return ((totalVal - compareVal) / compareVal) * 100;
        return undefined;
    };

    const formatTitle = (titleParam: any, totalParam: any) => {
        console.log(titleParam, "titleParam")
        switch (titleParam.toLowerCase()) {
            case t('dashboard.global.ad_cost').toLowerCase():
            case t('dashboard.global.search_ad_cost').toLowerCase():
            case t('dashboard.global.shopping_ad_cost').toLowerCase():
            case t('dashboard.global.pmax_ad_cost').toLowerCase():
                return formatCurrency(totalParam, currency, lang, true, 1);
            case t('dashboard.brandTraffic.blendedCtr').toLowerCase():
            case t('dashboard.global.organic_ctr').toLowerCase():
            case t('dashboard.global.paid_ctr').toLowerCase():
            case t('dashboard.global.shopping_paid_ctr').toLowerCase():
            case t('dashboard.global.search_paid_ctr').toLowerCase():
            case t('dashboard.global.pmax_paid_ctr').toLowerCase():
            case "search impression share":
            case "impr. (top) %":
            case "impr. (abs. top)%".toLowerCase():
            case "search lost top is (rank)".toLowerCase():
                const cappedTotal = Math.min(totalParam, 100);
                return cappedTotal === 0 ? '0.0%' : fPercent(cappedTotal);
            case t('dashboard.brandTraffic.brandSearches').toLowerCase():
            case t('dashboard.brandTraffic.totalBrandTraffic').toLowerCase():
            case t('dashboard.brandTraffic.organic_brand_traffic').toLowerCase():
            case t('dashboard.brandTraffic.ad_brand_traffic').toLowerCase():
            case t('dashboard.global.search_brand_traffic').toLowerCase():
            case t('dashboard.global.shopping_brand_traffic').toLowerCase():
            case t('dashboard.global.pmax_brand_traffic').toLowerCase():
                return formatNumber(totalParam);
            case t('dashboard.global.blended_cpc').toLowerCase():
            case t('dashboard.global.cpc').toLowerCase():
            case t('dashboard.global.search_cpc').toLowerCase():
            case t('dashboard.global.shopping_cpc').toLowerCase():
            case t('dashboard.global.pmax_cpc').toLowerCase():
                return formatCurrency(totalParam, currency, lang, false, 2);
            default:
                return totalParam >= 1
                    ? formatNumber(totalParam)
                    : formatNumber(totalParam * 1000);
        }
    };

    // Définition de toutes les définitions possibles
    const KPI_DEFINITIONS: Record<string, string> = {
        "brand_searches": t('definitions.brand_searches'),
        "total_brand_traffic": t('definitions.brand_traffic'),
        "organic_brand_traffic": t('definitions.organic_brand_traffic'),
        "ad_brand_traffic": t('definitions.paid_brand_traffic'),
        "blended_ctr": t('definitions.blended_ctr'),
        "uncaptured_brand_traffic": t('definitions.uncaptured_brand_traffic'),
        "paid_ctr": t('definitions.paid_ctr'),
        "search_paid_ctr": t('definitions.search_paid_ctr'),
        "organic_ctr": t('definitions.organic_ctr'),
        "uncaptured_percentage": t('definitions.uncaptured_percentage'),
        "blended_cpc": t('definitions.blended_cpc'),
        "cpc": t('definitions.cpc'),
        "search_cpc": t('definitions.search_cpc'),
        "ad_cost": t('definitions.ad_cost'),
        "search_ad_cost": t('definitions.search_ad_cost'),
        "shopping_paid_ctr": t('definitions.shopping_paid_ctr'),
        "shopping_ad_cost": t('definitions.shopping_ad_cost'),
        "shopping_cpc": t('definitions.shopping_cpc'),
    };

    const COST_KPIS = [
        'cpc',
        'blended_cpc',
        'search_cpc',
        'shopping_cpc',
        'ad_cost',
        'search_ad_cost',
        'shopping_ad_cost',
    ];

    const isCostKPI = COST_KPIS.includes(val);

    return (
        <Stack
            onClick={() => handleKPI(val)}
            sx={{
                cursor: 'pointer',
                position: 'relative',
                width: 1,
                borderTopLeftRadius: isFirst ? "12px" : 0,
                borderBottomLeftRadius: isFirst ? "12px" : 0,
                height: filterStore?.compare ? 120 : 100,
                overflow: 'hidden',
                border: '1px solid rgba(188, 188, 188, 0.20)',
                ...sx,
            }}
        >
            <Box
                sx={{
                    width: 235,
                    height: 235,
                    position: 'absolute',
                    top: -240,
                    left: '80%',
                    transform: 'translateX(-50%)',
                    bgcolor: palette?.lighter ?? color ?? theme.palette.primary.main,
                    opacity: palette?.lighter ? 0.9 : 0,
                    filter: 'blur(70px)',
                    borderRadius: '30%',
                    pointerEvents: 'none',
                }}
            />

            <Box sx={{ width: 1, height: 1, padding: '16px', position: 'relative' }}>
                <Stack spacing={1}>
                    <Box display={"flex"} flexDirection={'row'} justifyContent={"space-between"}>
                        <CustomTooltip title={KPI_DEFINITIONS[val]} placement="top">
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#637381',
                                    cursor: 'help',
                                    fontSize: 13,
                                    textDecoration: 'underline', textDecorationStyle: 'dotted'
                                }}
                            >
                                {title}
                            </Typography>
                        </CustomTooltip>

                        {filterStore.compare && (
                            <CompareChip disabled={!selected} value={compareValue(total, compare) ?? 0} invertedColor={isCostKPI} />
                        )}
                    </Box>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box display={"flex"} flexDirection={"column"} sx={{ width: '100%' }}>
                            <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                                <Box display={"flex"} flexDirection={"column"}>

                                    <Typography variant="h4" sx={{ color: selected ? '#333C47' : "rgba(177, 185, 196, 0.9)", fontWeight: 700, fontSize: 24 }}>
                                        {formatValue(total, isPercent ?? false)}
                                    </Typography>

                                    {filterStore.compare && (
                                        <Typography color={selected ? '#919EAB' : "rgba(177, 185, 196, 0.9)"} fontSize={12}>
                                            {filterStore?.start && filterStore?.end
                                                ? `${new Date(filterStore.start).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} - ${new Date(filterStore.end).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`
                                                : ''}
                                        </Typography>
                                    )}

                                </Box>

                                {filterStore.compare && (
                                    <Box display={"flex"} flexDirection={"column"}>
                                        <Box display={"flex"} flexDirection={"row"}>
                                            <Typography position={"relative"} top={13} mr={"6px"} color={selected ? '#333C47' : "rgba(177, 185, 196, 0.9)"} fontSize={12}>vs.</Typography>
                                            <Typography variant="h4" sx={{ color: selected ? 'rgba(51,60,71,1)' : "rgba(177, 185, 196, 0.9)", fontWeight: 700, fontSize: 24 }}>{formatValue(compare, isPercent ?? false)}</Typography>
                                        </Box>
                                        <Typography color={selected ? '#919EAB' : "rgba(177, 185, 196, 0.9)"} fontSize={12}>
                                            {filterStore?.start && filterStore?.end
                                                ? `${new Date(filterStore.compareStart).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} - ${new Date(filterStore.compareEnd).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`
                                                : ''}
                                        </Typography>
                                    </Box>
                                )}

                            </Box>
                        </Box>
                    </Stack>
                </Stack>
            </Box>
        </Stack>
    );
}